"use client";

import { useQuery } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks/shared/useAccessToken";
import { NotificationsApi } from "@/openapi/generated";
import { parseNumericLastSelectedId } from "@/lib/pagination/scrollPageUrl";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import { NOTIFICATIONS_REFETCH_INTERVAL_MS } from "@/features/notifications/constants";
import { useNotificationsRefetchInterval } from "./useNotificationsRefetchInterval";

interface UseNotificationsGetProps {
  userId: number;
  page?: number;
  pageSize?: number;
  lastSelectedId?: string | number;
  active?: boolean;
  refetchInterval?: number | false;
}

export const useNotificationsGet = ({
  userId,
  page = 1,
  pageSize = 100,
  lastSelectedId,
  active,
  refetchInterval = NOTIFICATIONS_REFETCH_INTERVAL_MS,
}: UseNotificationsGetProps) => {
  const accessToken = useAccessToken();
  const effectiveRefetchInterval =
    useNotificationsRefetchInterval(refetchInterval);

  const { data, isError, isLoading, isSuccess, isPending, isFetching, refetch } =
    useQuery({
    queryKey: [
      QueryKeys.Notifications,
      userId,
      page,
      pageSize,
      lastSelectedId,
      active,
    ],
    queryFn: () => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(NotificationsApi, accessToken);
      return service.apiNotificationsUserIdGet({
        userId,
        page,
        pageSize,
        lastSelectedId: parseNumericLastSelectedId(lastSelectedId),
        active,
      });
    },
    enabled: !!accessToken && !!userId,
    refetchOnMount: false,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
    staleTime: 30 * 1000,
    refetchInterval: effectiveRefetchInterval,
  });

  return {
    data,
    isError,
    isLoading,
    isSuccess,
    isPending,
    isFetching,
    refetch,
  };
};
